import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Send, 
  X, 
  Hash, 
  Users, 
  MessageCircle, 
  Settings,
  Smile,
  Image,
  Paperclip
} from 'lucide-react'
import { useSocket } from '../hooks/useSocket'
import { useAuthStore } from '../hooks/useAuthStore'
import { useTranslation } from 'react-i18next'

const ChatPanel = ({ onClose }) => {
  const { t } = useTranslation('common')
  const [activeTab, setActiveTab] = useState('global')
  const [message, setMessage] = useState('')
  // userId of the selected private-chat partner ('' = none selected)
  const [privateRecipient, setPrivateRecipient] = useState('')
  const messagesEndRef = useRef(null)

  const { messages, sendMessage, onlineUsers, currentGame, markMessagesRead } = useSocket()
  const { user } = useAuthStore()

  const otherUsers = onlineUsers.filter(u => u.userId !== user?.id)
  const recipientUser = otherUsers.find(u => u.userId === privateRecipient)

  const tabs = [
    { id: 'global', name: t('chat.global'), icon: Hash, color: 'text-blue-400' },
    { id: 'game', name: t('chat.game'), icon: Users, color: 'text-green-400', disabled: !currentGame },
    { id: 'private', name: t('chat.private'), icon: MessageCircle, color: 'text-purple-400' }
  ]

  const filteredMessages = messages.filter(msg => {
    switch (activeTab) {
      case 'global':
        return msg.type === 'global'
      case 'game':
        return msg.type === 'game' && msg.gameId === currentGame?.id
      case 'private':
        if (msg.type !== 'private') return false
        if (msg.from !== user?.id && msg.recipient !== user?.id) return false
        // With a partner selected, narrow to that conversation
        if (privateRecipient) {
          return msg.from === privateRecipient || msg.recipient === privateRecipient
        }
        return true
      default:
        return false
    }
  })

  useEffect(() => {
    scrollToBottom()
  }, [messages, activeTab])

  // Panel is open, so everything received counts as read
  useEffect(() => {
    markMessagesRead()
  }, [messages, markMessagesRead])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (!message.trim()) return

    let recipient = null
    let gameId = null

    switch (activeTab) {
      case 'game':
        gameId = currentGame?.id
        break
      case 'private':
        if (!privateRecipient.trim()) return
        recipient = privateRecipient.trim()
        break
    }

    sendMessage(activeTab, message.trim(), gameId, recipient)
    setMessage('')
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getUserColor = (userId) => {
    const colors = [
      'text-red-400', 'text-blue-400', 'text-green-400', 'text-yellow-400',
      'text-purple-400', 'text-pink-400', 'text-indigo-400', 'text-cyan-400'
    ]
    const index = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[index % colors.length]
  }

  return (
    <div className="h-full flex flex-col bg-gray-800 border-l border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{t('chat.title')}</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-700">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`flex-1 p-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? `${tab.color} border-b-2 border-current`
                  : tab.disabled
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.name}</span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Private Chat Recipient */}
      {activeTab === 'private' && (
        <div className="p-3 bg-gray-700 border-b border-gray-600">
          <select
            value={privateRecipient}
            onChange={(e) => setPrivateRecipient(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">
              {otherUsers.length === 0 ? t('chat.noOtherPlayers') : t('chat.selectPlayer')}
            </option>
            {otherUsers.map(u => (
              <option key={u.userId} value={u.userId}>
                {u.username}
              </option>
            ))}
          </select>
          {recipientUser && (
            <div className="mt-2 text-xs text-gray-400">
              {t('chat.chatWith')} <span className="text-purple-400">{recipientUser.username}</span>
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence>
          {filteredMessages.map((msg, index) => {
            const isOwn = msg.from === user?.id
            return (
              <motion.div
                key={`${msg.id}-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  {!isOwn && (
                    <span className={`font-medium text-sm ${getUserColor(msg.from)}`}>
                      {msg.username || msg.from}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {formatTime(msg.timestamp)}
                  </span>
                  {msg.type === 'private' && (
                    <span className="text-xs text-purple-400 bg-purple-400/10 px-1.5 py-0.5 rounded">
                      {isOwn && msg.recipientUsername
                        ? `${t('chat.private')} → ${msg.recipientUsername}`
                        : t('chat.private')}
                    </span>
                  )}
                </div>

                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm break-words ${
                    isOwn
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-gray-700 text-gray-100 rounded-bl-md'
                  }`}
                >
                  {msg.content}
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
        
        {filteredMessages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-400 text-sm">
              {activeTab === 'private' && !privateRecipient
                ? t('chat.selectPlayerToStart')
                : t('chat.noMessagesYet')}
            </p>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-700">
        <form onSubmit={handleSendMessage} className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  activeTab === 'private' && !privateRecipient
                    ? t('chat.selectPlayerToStart')
                    : t('chat.typeMessage')
                }
                disabled={activeTab === 'private' && !privateRecipient}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                maxLength={500}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-1">
                <button
                  type="button"
                  className="p-1 text-gray-400 hover:text-white transition-colors"
                  title="Emoji"
                >
                  <Smile className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={!message.trim() || (activeTab === 'private' && !privateRecipient)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div>
              {message.length}/500
            </div>
            <div className="flex gap-2">
              <span>{t('chat.pressEnterToSend')}</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChatPanel